import { HubConnectionState, type HubConnection } from '@microsoft/signalr';
import {
    createSlice, createEntityAdapter, PayloadAction,
} from '@reduxjs/toolkit';

interface SignalRHub {
    readonly id: string;
    readonly uuids: string[];
    readonly hub: HubConnection;
    readonly hubState: HubConnectionState;
}

interface SignalRPayload {
    readonly id: string;
    readonly uuid: string;
    readonly hub: HubConnection;
}

interface PartialSignalRPayload {
    readonly id: string;
    readonly uuid: string;
}

export const signalRAdapter = createEntityAdapter({
    selectId: (hub: SignalRHub) => hub.id,
});

export const signalRSlice = createSlice({
    name: 'signalR',
    initialState: signalRAdapter.getInitialState(),
    reducers: {
        addHub: (state, action: PayloadAction<SignalRPayload>) => {
            const hub: SignalRHub = {
                id: action.payload.id,
                hub: action.payload.hub,
                uuids: [action.payload.uuid],
                hubState: HubConnectionState.Connecting,
            };

            signalRAdapter.addOne(state, hub);
        },
        addToHub: (state, action: PayloadAction<PartialSignalRPayload>) => {
            if (state.entities[action.payload.id]) {
                state.entities[action.payload.id].uuids.push(action.payload.uuid);
            }
        },
        removeHub: (state, action: PayloadAction<string>) => {
            // Search by unique id
            const hub = Object.values(state.entities).find((entity) => entity.uuids.includes(action.payload));

            if (hub) {
                if (hub.uuids.length > 1) {
                    state.entities[hub.id].uuids = state.entities[hub.id].uuids.filter((uuid) => uuid !== action.payload);
                } else {
                    // Stop a connection that is still trying to reconnect too, or its retries outlive the last user.
                    if (hub.hub.state !== HubConnectionState.Disconnected) {
                        hub.hub.stop();
                    }

                    signalRAdapter.removeOne(state, hub.id);
                }
            }
        },
        /** Swaps in a new connection after the old one closed, keeping everyone using the hub. */
        replaceHub: (state, action: PayloadAction<Pick<SignalRHub, 'id' | 'hub'>>) => {
            const { id, hub } = action.payload;

            signalRAdapter.updateOne(state, { id, changes: { hub, hubState: HubConnectionState.Connecting } });
        },
        updateHubState: (state, action: PayloadAction<Pick<SignalRHub, 'id' | 'hubState'>>) => {
            const { id, hubState } = action.payload;

            signalRAdapter.updateOne(state, { id, changes: { hubState } });
        },
    },
});

export const {
    addHub, addToHub, removeHub, replaceHub, updateHubState,
} = signalRSlice.actions;
export default signalRSlice.reducer;